// Mapeia a saída do parser determinístico (FaturaEstruturada) para os campos do
// formulário da Análise Técnica (UcAnalysisModal). Função PURA — nenhum acesso a
// rede/estado — pra poder ser testada isoladamente com fixtures reais.
//
// Regra de ouro: só sugerir o que a fatura REALMENTE imprime. O que não consta
// vai em `naoEncontrados` e o painel avisa que segue manual, em vez de preencher
// com chute. A fatura Equatorial traz mês + consumo faturado no histórico de 12
// meses, mas NÃO traz injeção, vencimento nem pagamento por mês — isso o analista
// lê no portal da Equatorial.

import type { FaturaEstruturada } from '../../../../types';

export interface LinhaHistoricoSugerida {
  month: string;    // 'mm/aa' (formato que o modal valida)
  consumo: number;
}

export interface CampoSugerido {
  /** chave em activeUc (ex.: 'titularidade') */
  campo: string;
  /** rótulo como aparece no formulário */
  label: string;
  valor: string;
}

export interface CheckSugerido {
  campo: string;
  label: string;
  valor: boolean;
  /** por que o robô sugeriu isso — o analista precisa poder discordar com contexto */
  motivo: string;
}

export interface SugestaoAnaliseTecnica {
  campos: CampoSugerido[];
  historico: LinhaHistoricoSugerida[];
  checks: CheckSugerido[];
  /** rótulos dos campos que a fatura não fornece — exibidos como "segue manual" */
  naoEncontrados: string[];
}

const MESES: Record<string, string> = {
  JAN: '01', FEV: '02', MAR: '03', ABR: '04', MAI: '05', JUN: '06',
  JUL: '07', AGO: '08', SET: '09', OUT: '10', NOV: '11', DEZ: '12',
};

/** 'JUN/26' → '06/26'. O modal valida historyTable.month com /^\d{2}\/\d{2}$/ e
 *  descarta qualquer outra coisa, então converter aqui é obrigatório. */
export function mesAnoParaMmAa(mesAno: string): string {
  const m = /^([A-Za-zÇç]{3})\/(\d{2,4})$/.exec(String(mesAno || '').trim());
  if (!m) return '';
  const mes = MESES[m[1].toUpperCase()];
  if (!mes) return '';
  return `${mes}/${m[2].slice(-2)}`;
}

/** Extrai o DIA de uma data do parser. O modal guarda só o DIA das datas de ciclo
 *  (a data cheia muda todo mês; o dia é o que caracteriza o ciclo da UC).
 *  O parser devolve ISO ('2026-07-10'); aceita BR também por segurança, porque o
 *  layout espelho (parse-old) nem sempre normaliza. */
export function diaDaData(data: string): string {
  const s = String(data || '').trim();
  const iso = /^\d{4}-(\d{2})-(\d{2})$/.exec(s);
  if (iso) return iso[2];
  const br = /^(\d{2})\/\d{2}\/\d{4}$/.exec(s);
  return br ? br[1] : '';
}

/** Normaliza uma data do parser para ISO 'aaaa-mm-dd' (o <input type="date"> só
 *  aceita ISO). Já-ISO passa direto; BR é convertida. */
export function paraIso(data: string): string {
  const s = String(data || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
}

/** UC sem máscara e sem zeros à esquerda ('39.601.012-45' → '3960101245'), igual
 *  ao que mapeador-faturas grava em faturas.uc_codigo — assim o valor sugerido
 *  compara de igual pra igual com unidades_consumidoras.numero_uc. */
export function ucSemMascara(uc: string): string {
  return String(uc || '').replace(/[.\-\s]/g, '').replace(/^0+/, '');
}

/** Número do parser → number. Trata o glitch conhecido da Equatorial, que imprime
 *  '4242.00' no lugar de '4242,00' (ponto onde deveria ser vírgula decimal): sem
 *  isso o consumo vira 424200 kWh. É a mesma defesa que o parseBrNumber do
 *  UcAnalysisModal já faz na entrada manual. */
export function numeroBr(valor: string): number {
  let s = String(valor ?? '').trim();
  if (!s) return 0;
  if (/\.\d{2}$/.test(s) && !s.includes(',')) s = s.slice(0, -3); // '4242.00' → '4242'
  const n = parseFloat(s.replace(/\./g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
}

const GRUPOS_VALIDOS = ['A1', 'A2', 'A3', 'A4', 'AS', 'B1', 'B2', 'B3', 'B4'];

/** O parser devolve sub_grupo cru ('B3', 'B OPT B3', 'AS'). O <select> do modal só
 *  aceita a lista fechada acima — devolver '' é melhor que sujar o campo. */
export function normalizarGrupo(subGrupo: string, grupo: string): string {
  const cru = String(subGrupo || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const achado = GRUPOS_VALIDOS.find((g) => cru.includes(g));
  if (achado) return achado;
  // Sem sub-grupo legível, não dá pra adivinhar o número (B1 vs B3 muda tarifa).
  return GRUPOS_VALIDOS.includes(String(grupo || '').toUpperCase()) ? String(grupo).toUpperCase() : '';
}

/** 'TRIFASICO' / 'TRIFÁSICA' / 'TRI' → 'Trifásico' (as 3 opções do <select>). */
export function normalizarTipoLigacao(tipo: string): string {
  const t = String(tipo || '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  if (t.includes('TRIF') || t.startsWith('TRI')) return 'Trifásico';
  if (t.includes('BIF') || t.startsWith('BI')) return 'Bifásico';
  if (t.includes('MONO')) return 'Monofásico';
  return '';
}

/** A UC é considerada com GD quando a fatura mostra energia injetada/compensada:
 *  presença de bloco SCEE ou de item marcado com tipo_gd. */
export function detectaGd(e: FaturaEstruturada): boolean {
  if ((e.scee || []).length > 0) return true;
  return (e.itens || []).some((i) => String(i.tipo_gd || '').trim() !== '');
}

const LIMITE_CONSUMO_KWH = 500;

export function mapEstruturadoParaAnaliseTecnica(
  e: FaturaEstruturada,
  opts: { incluirNascimento?: boolean } = {},
): SugestaoAnaliseTecnica {
  const cab = e?.cabecalho || {};
  const campos: CampoSugerido[] = [];
  const naoEncontrados: string[] = [];

  const push = (campo: string, label: string, valor: string) => {
    if (valor) campos.push({ campo, label, valor });
    else naoEncontrados.push(label);
  };

  push('titularidade', 'Nome do Titular', String(cab.nome_cliente || '').trim());
  push('unidadeConsumidora', 'Unidade Consumidora', ucSemMascara(cab.unidade_consumidora));
  push('nParceiro', 'Nº Parceiro', String(cab.numero_parceiro || '').trim());
  push('grupo', 'Grupo Tarifário', normalizarGrupo(cab.sub_grupo, cab.grupo));
  push('tipoFornecimento', 'Tipo de Ligação', normalizarTipoLigacao(cab.tipo_fornecimento));
  push('dataLeitura', 'Dia Leitura', diaDaData(cab.data_leitura_atual));
  push('dataApresentacao', 'Dia Apresentação', diaDaData(cab.apresentacao));
  push('dataVencimento', 'Dia Vencimento', diaDaData(cab.vencimento));
  if (opts.incluirNascimento) {
    push('dataNascimentoCliente', 'Data de Nascimento', paraIso(cab.data_nascimento));
  }

  // Campos que simplesmente não existem na fatura — declarar em vez de omitir,
  // pra o analista saber que ainda precisa preenchê-los à mão.
  naoEncontrados.push('Dia Processamento (não consta na fatura)');
  naoEncontrados.push('Novo Nº UC (não consta na fatura)');

  // Histórico: mês + consumo faturado. Deduplica por mês (OCR às vezes repete a
  // linha) e corta em 12, que é o tamanho da grade.
  const vistos = new Set<string>();
  const historico: LinhaHistoricoSugerida[] = [];
  for (const h of e?.historico || []) {
    const month = mesAnoParaMmAa(h.mes_ano);
    if (!month || vistos.has(month)) continue;
    vistos.add(month);
    historico.push({ month, consumo: numeroBr(h.consumo_faturado) });
    if (historico.length === 12) break;
  }
  if (!historico.length) naoEncontrados.push('Histórico de 12 meses');
  naoEncontrados.push('Injeção / Vencimento / Pagamento por mês (só no portal Equatorial)');

  // Checks: sugestões derivadas, sempre com o motivo à vista. Os demais itens do
  // checklist (fatura atualizada, benefício, titular confere, distrato) dependem
  // de conferência externa e continuam em branco de propósito.
  const checks: CheckSugerido[] = [];

  const grupoCru = String(cab.grupo || '').toUpperCase();
  if (grupoCru === 'A' || grupoCru === 'B') {
    checks.push({
      campo: 'grupoB',
      label: 'A Unidade é Grupo B?',
      valor: grupoCru === 'B',
      motivo: `Fatura indica Grupo ${grupoCru}${cab.sub_grupo ? ` (${cab.sub_grupo})` : ''}`,
    });
  }

  if (historico.length) {
    const media = historico.reduce((s, h) => s + h.consumo, 0) / historico.length;
    checks.push({
      campo: 'consumo500',
      label: 'Consumo Compensável > 500 kWh?',
      valor: media > LIMITE_CONSUMO_KWH,
      motivo: `Média de ${Math.round(media).toLocaleString('pt-BR')} kWh em ${historico.length} ${historico.length === 1 ? 'mês' : 'meses'}`,
    });
  }

  const temGd = detectaGd(e);
  checks.push({
    campo: 'possuiGD',
    label: 'A Unidade possui GD instalada?',
    valor: temGd,
    motivo: temGd
      ? `Fatura traz ${(e.scee || []).length || 'itens de'} bloco(s) de energia compensada (SCEE/GD)`
      : 'Nenhum bloco SCEE ou item de GD na fatura',
  });

  return { campos, historico, checks, naoEncontrados };
}
